import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { House, Compass, Gift, Users, User, LogOut, Shield, Settings as SettingsIcon, BookHeart, LifeBuoy } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/NotificationBell";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useLang, LangToggle } from "@/lib/i18n";

const navItems = [
  { to: "/dashboard", key: "nav_home", icon: House },
  { to: "/kesfet", key: "nav_explore", icon: Compass },
  { to: "/listelerim", key: "nav_wishlist", icon: Gift },
  { to: "/arkadaslar", key: "nav_friends", icon: Users },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const location = useLocation();
  const navigate = useNavigate();

  const profileTo = user ? `/${user.username}` : "/giris";

  return (
    <div className="min-h-screen pb-20 lg:pb-0">
      <header className="sticky top-0 z-40 glass-nav border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2" data-testid="header-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 flex items-center justify-center shadow-[0_2px_10px_rgba(99,102,241,0.4)]">
              <Gift size={20} className="text-white" />
            </div>
            <span className="font-heading font-extrabold text-lg tracking-tight gradient-text">hediyeniiste</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(({ to, key, icon: Icon }) => (
              <Link key={to} to={to} data-testid={`nav-${to.slice(1)}`}
                className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 transition-colors ${
                  location.pathname === to ? "btn-gradient" : "text-muted-foreground hover:bg-secondary"}`}>
                <Icon size={16} /> {t(key)}
              </Link>
            ))}
          </nav>
          {user ? (
            <div className="flex items-center gap-2">
              <LangToggle />
              <NotificationBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="header-avatar-menu" className="rounded-full focus-visible:ring-2 focus-visible:ring-primary">
                  <Avatar className="w-9 h-9 border-2 border-primary/30">
                    <AvatarImage src={user.picture} />
                    <AvatarFallback className="bg-secondary text-sm font-semibold">{user.name?.[0]}</AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem data-testid="menu-profile" onClick={() => navigate(profileTo)}>
                  <User size={15} className="mr-2" /> {t("my_profile")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-memories" onClick={() => navigate("/anilar")}>
                  <BookHeart size={15} className="mr-2" /> {t("memories")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-settings" onClick={() => navigate("/ayarlar")}>
                  <SettingsIcon size={15} className="mr-2" /> {t("settings")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="menu-support" onClick={() => navigate("/iletisim")}>
                  <LifeBuoy size={15} className="mr-2" /> {t("nav_support")}
                </DropdownMenuItem>
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => navigate("/admin")}>
                    <Shield size={15} className="mr-2" /> {t("admin_panel")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut size={15} className="mr-2" /> {t("logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          ) : (
            <Link to="/giris" data-testid="header-login-btn"
              className="px-5 py-2 rounded-full btn-gradient text-sm font-semibold active:scale-95">
              {t("login")}
            </Link>
          )}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-40 glass-nav border-t border-border/50" data-testid="bottom-nav">
        <div className="flex items-center justify-around h-16">
          {[...navItems.map((n) => ({ ...n, label: t(n.key) })), { to: profileTo, label: t("nav_profile"), icon: User }].map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link key={label} to={to} aria-label={label} data-testid={`bottomnav-${to.replace(/\//g, "") || "home"}`}
                className={`flex flex-col items-center gap-0.5 px-3 py-1 transition-all ${active ? "text-primary scale-105" : "text-muted-foreground"}`}>
                <Icon size={22} strokeWidth={active ? 2.5 : 2} />
                <span className="text-[10px] font-medium">{label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
